import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_BASE } from '../../lib/apiOrigin';
import { workerEnrollmentCopy } from './workerEnrollmentCopy';
import { consumeWorkerVerificationFragment, takeWorkerVerificationProof } from './workerEnrollmentLinks';

const client = axios.create();
export const WORKER_ENROLLMENT_STATUS_TIMEOUT_MS = 10000;
const field = 'mt-2 w-full min-w-0 rounded border border-white/25 bg-zinc-950 p-3 text-white focus:outline-none focus:ring-2 focus:ring-sky-400';
const button = 'inline-flex min-h-[48px] items-center justify-center rounded border border-sky-400 px-5 py-3 text-sky-200 hover:bg-sky-500/15 focus:outline-none focus:ring-2 focus:ring-sky-300 disabled:opacity-50';

export default function WorkerEnrollment({ apiBase = API_BASE, lang = 'en', onAuthenticated, onAvailabilityChange }) {
  const t = workerEnrollmentCopy[lang] || workerEnrollmentCopy.en;
  const [availability, setAvailability] = useState('loading');
  const [purpose, setPurpose] = useState('register');
  const [proof, setProof] = useState('');
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const alive = useRef(false), generation = useRef(0), lock = useRef(false);
  const proofRef = useRef('');
  const callback = useRef(onAuthenticated), availabilityCallback = useRef(onAvailabilityChange), availabilityRequest = useRef(0), availabilityTimer = useRef(null);
  callback.current = onAuthenticated; availabilityCallback.current = onAvailabilityChange;

  function updateAvailability(status) {
    if (!alive.current) return;
    setAvailability(status); availabilityCallback.current?.(status);
  }

  async function checkAvailability() {
    const request = ++availabilityRequest.current;
    clearTimeout(availabilityTimer.current);
    updateAvailability('loading');
    const timer = setTimeout(() => {
      if (alive.current && request === availabilityRequest.current) {
        availabilityRequest.current += 1;
        updateAvailability('unavailable');
      }
    }, WORKER_ENROLLMENT_STATUS_TIMEOUT_MS);
    availabilityTimer.current = timer;
    try {
      const result = await client.get(`${apiBase}/worker-enrollment/status`, { timeout: WORKER_ENROLLMENT_STATUS_TIMEOUT_MS });
      if (!alive.current || request !== availabilityRequest.current) return;
      const status = result?.data?.status;
      updateAvailability(result?.data?.available === true && status === 'available' ? 'available'
        : ['disabled', 'provider_not_configured'].includes(status) ? status : 'unavailable');
    } catch { if (alive.current && request === availabilityRequest.current) updateAvailability('unavailable'); }
    finally { clearTimeout(timer); if (availabilityTimer.current === timer) availabilityTimer.current = null; }
  }

  useEffect(() => {
    alive.current = true;
    const consume = () => {
      const incoming = consumeWorkerVerificationFragment();
      if (!incoming.present) return;
      generation.current += 1; lock.current = false;
      proofRef.current = incoming.token; setProof(incoming.token);
      setBusy(''); setNotice(''); setError(incoming.invalid ? 'invalid' : '');
    };
    consume();
    checkAvailability();
    window.addEventListener('hashchange', consume);
    window.addEventListener('popstate', consume);
    return () => {
      alive.current = false; generation.current += 1; availabilityRequest.current += 1; clearTimeout(availabilityTimer.current); availabilityTimer.current = null;
      window.removeEventListener('hashchange', consume);
      window.removeEventListener('popstate', consume);
    };
    // API base is fixed for this page; language changes do not restart proof.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  async function revokeDiscardedSession(data) {
    if (typeof data?.session_token !== 'string' || !data.session_token) return;
    try { await client.post(`${apiBase}/installer/logout`, {}, { headers: { Authorization: `Bearer ${data.session_token}` } }); }
    catch { /* The orphaned session also has a bounded server expiry. */ }
  }

  async function submit(event) {
    event.preventDefault();
    if (lock.current || availability !== 'available') return;
    const form = event.currentTarget;
    if (!form.reportValidity()) return;
    const data = new FormData(form);
    if (data.get('consent') !== 'on' || !String(data.get('email') || '').trim()
        || (purpose === 'register' && String(data.get('name') || '').trim().length < 2)) { setError('required'); return; }
    const current = ++generation.current;
    lock.current = true; setBusy('request'); setNotice(''); setError('');
    try {
      await client.post(`${apiBase}/worker-enrollment/request`, {
        purpose, email: String(data.get('email')).trim(), name: purpose === 'register' ? String(data.get('name')).trim() : '',
        language: ['en', 'pt', 'es'].includes(lang) ? lang : 'en', consent: true,
      });
      if (alive.current && current === generation.current) { setNotice('received'); form.reset(); }
    } catch (err) {
      if (alive.current && current === generation.current) {
        setError('error'); if (err?.response?.status === 503) updateAvailability('unavailable');
      }
    } finally {
      if (alive.current && current === generation.current) { lock.current = false; setBusy(''); }
    }
  }

  async function verify() {
    if (lock.current || availability !== 'available' || !proofRef.current) return;
    const token = proofRef.current;
    proofRef.current = ''; setProof('');
    if (!takeWorkerVerificationProof(token)) { setError('invalid'); return; }
    const current = ++generation.current;
    lock.current = true; setBusy('verify'); setNotice(''); setError('');
    let result;
    try {
      result = await client.post(`${apiBase}/worker-enrollment/verify`, { token });
      if (!alive.current || current !== generation.current) { await revokeDiscardedSession(result?.data); return; }
      if (typeof result?.data?.session_token !== 'string' || !result.data.session_token || typeof callback.current !== 'function') throw new Error('Invalid verification response');
      callback.current(result.data);
    } catch {
      if (result?.data?.session_token) await revokeDiscardedSession(result.data);
      if (alive.current && current === generation.current) setError('invalid');
    } finally {
      if (alive.current && current === generation.current) { lock.current = false; setBusy(''); }
    }
  }

  function cancel() {
    takeWorkerVerificationProof(proofRef.current);
    generation.current += 1; lock.current = false; proofRef.current = '';
    setProof(''); setBusy(''); setError(''); setNotice('canceled');
  }
  const available = availability === 'available';
  const unavailableCopy = availability === 'loading' ? t.checking : availability === 'disabled' ? t.disabled : availability === 'provider_not_configured' ? t.provider : t.unavailable;
  return <section className="space-y-5 rounded border border-sky-500/40 bg-sky-500/5 p-5 sm:p-6" aria-label={t.title}>
    <div><h2 className="text-xl font-bold">{t.title}</h2><p className="mt-2 text-zinc-300">{t.intro}</p></div>
    <p className="text-sm text-zinc-300">{t.gate}</p>
    {!available && <div className="space-y-3"><p role="status">{unavailableCopy}</p>{availability !== 'loading' && <button type="button" className={button} onClick={checkAvailability}>{t.refresh}</button>}</div>}
    {error && <p role="alert" className="text-red-300">{t[error]}</p>}
    {notice && <p role="status" className="text-sky-200">{t[notice]}</p>}
    {(proof || busy === 'verify') && <div className="space-y-3 rounded border border-sky-300 p-4">
      <h3 className="font-bold">{t.verifyTitle}</h3><p>{t.verifyText}</p>
      <div className="flex flex-wrap gap-3"><button type="button" className={button} disabled={!available || !!busy} onClick={verify}>{busy === 'verify' ? t.verifying : t.verify}</button><button type="button" className={button} onClick={cancel}>{t.cancel}</button></div>
    </div>}
    {available && !proof && busy !== 'verify' && <form onSubmit={submit} className="space-y-5">
      <fieldset disabled={!!busy} className="min-w-0 space-y-5">
        <div className="flex flex-wrap gap-3"><button type="button" className={button} aria-pressed={purpose === 'register'} onClick={() => { setPurpose('register'); setError(''); setNotice(''); }}>{t.register}</button><button type="button" className={button} aria-pressed={purpose === 'access'} onClick={() => { setPurpose('access'); setError(''); setNotice(''); }}>{t.access}</button></div>
        {purpose === 'register' && <label className="block">{t.name}<input className={field} type="text" name="name" autoComplete="name" minLength={2} maxLength={120} required /></label>}
        <label className="block">{t.email}<input className={field} type="email" name="email" autoComplete="email" maxLength={254} required /></label>
        <p className="text-sm text-zinc-300">{t.privacy}</p>
        <label className="flex items-start gap-3 text-sm text-zinc-300"><input name="consent" type="checkbox" required className="mt-1 h-5 w-5 shrink-0 accent-sky-500" />{t.consent}</label>
        <button className={button} type="submit" disabled={!!busy}>{busy === 'request' ? t.sending : t.send}</button>
      </fieldset>
    </form>}
  </section>;
}
